import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { computed, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';

export type ThemeMode = 'light' | 'dark' | 'system';

export const THEME_STORAGE_KEY = 'vapt.theme';
export const DEFAULT_THEME_MODE: ThemeMode = 'light';

const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * Light / dark / system theme.
 *
 * - Persists the chosen mode in localStorage (`vapt.theme`).
 * - Applies the `.dark` class on `<html>` (Tailwind `dark:` variant + Spartan tokens).
 * - In `system` mode it follows `prefers-color-scheme` live.
 * - SSR-safe: on the server it only keeps the signal state, never touches DOM/storage.
 * - Default is `light` (legacy screens are not dark-ready yet). A tiny inline script in
 *   `index.html` applies the class before Angular boots to avoid a flash; it uses the same
 *   key/default.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private readonly _mode = signal<ThemeMode>(DEFAULT_THEME_MODE);
  private readonly _systemDark = signal(false);
  private initialized = false;

  /** Chosen mode (what the user picked). */
  readonly mode = this._mode.asReadonly();

  /** Effective theme after resolving `system`. */
  readonly resolved = computed<'light' | 'dark'>(() => {
    const mode = this._mode();
    if (mode === 'system') {
      return this._systemDark() ? 'dark' : 'light';
    }
    return mode;
  });

  readonly isDark = computed(() => this.resolved() === 'dark');

  /** Reads the stored mode, starts following the OS preference and applies the theme. Idempotent. */
  init(): void {
    if (this.initialized || !this.isBrowser) {
      return;
    }
    this.initialized = true;

    this._mode.set(this.readStoredMode());

    const media = this.matchMedia();
    if (media) {
      this._systemDark.set(media.matches);
      media.addEventListener('change', (event) => {
        this._systemDark.set(event.matches);
        this.apply();
      });
    }

    this.apply();
  }

  setMode(mode: ThemeMode): void {
    this._mode.set(mode);
    if (!this.isBrowser) {
      return;
    }
    try {
      localStorage.setItem(THEME_STORAGE_KEY, mode);
    } catch {
      // storage unavailable (private mode / quota): keep the in-memory value only
    }
    this.apply();
  }

  private apply(): void {
    this.document.documentElement.classList.toggle('dark', this.isDark());
  }

  private readStoredMode(): ThemeMode {
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored === 'light' || stored === 'dark' || stored === 'system') {
        return stored;
      }
    } catch {
      // ignore: fall back to default
    }
    return DEFAULT_THEME_MODE;
  }

  private matchMedia(): MediaQueryList | null {
    const view = this.document.defaultView;
    return view && typeof view.matchMedia === 'function' ? view.matchMedia(DARK_QUERY) : null;
  }
}
